import type { LayerKey } from '@festina-life/api'
import { dateUtils } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import * as Yup from 'yup'

import { getGlMapOperationFromDate } from '../../uiModels/glMapOperations/helpers'
import { useGlMapsForBlockAndLayer } from '../../uiModels/glMapOperations/useGlMapsForBlockAndLayer'
import type { CreateGlMapHideDraftInput } from './types'

const MINIMUM_MAPPING_ID_LENGTH = 2

export const useGlMapHideDraftValidationSchema = ({
  layer,
  block,
}: {
  layer: LayerKey
  block: string
}) => {
  const translate = useTranslate()
  const glMapsForBlockAndLayer = useGlMapsForBlockAndLayer({ layer, block })

  return Yup.object({
    mappingId: Yup.string()
      .required(translate('validation.required'))
      .min(
        MINIMUM_MAPPING_ID_LENGTH,
        translate('validation.length', { length: MINIMUM_MAPPING_ID_LENGTH }),
      )
      .noSpaces(translate('validation.noSpaces')),
    fromDate: Yup.string()
      .test({
        test: (value) =>
          !value || !isNaN(dateUtils.dateUtcAsLocal(value).valueOf()),
        message: translate('validation.invalidDate'),
      })
      .test(
        'nonExistentHideOperation',
        translate('validation.duplicatedOperation'),
        (value, context) => {
          const formInput = context.parent as Partial<CreateGlMapHideDraftInput>
          const currentMappingId = formInput.mappingId

          const matchingGlMap = currentMappingId
            ? glMapsForBlockAndLayer?.get(currentMappingId)
            : undefined

          return !(matchingGlMap?.operations ?? []).some(
            (entry) =>
              entry.isDraft &&
              getGlMapOperationFromDate(entry.internalOperation) == value,
            // Purposely using == so that undefined and null are treated the same, since
            // the from date is optional.
          )
        },
      ),
  })
}
